import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { KeyRound, Lock, MailIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

export function LoginPage(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, login, validate2FA } = useAuth();
  const { error, loading, isAuthenticated, requiresTwoFactor } = state;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");

  useEffect(() => {
    if (isAuthenticated) {
      const redirect = searchParams.get("redirect");
      if (redirect) {
        navigate(redirect, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [isAuthenticated, navigate, searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await login({ email, password });
  };

  const handleTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await validate2FA(twoFactorCode);
  };

  const twoFactorContent = (
    <form className="space-y-6" onSubmit={handleTwoFactorSubmit}>
      <div>
        <label
          htmlFor="twoFactorCode"
          className="block text-sm font-medium text-muted-foreground dark:text-gray-300 mb-1"
        >
          Cod de Verificare
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <KeyRound className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="twoFactorCode"
            name="twoFactorCode"
            type="text"
            required
            className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-foreground dark:text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
            placeholder="000000"
            value={twoFactorCode}
            onChange={(e) => setTwoFactorCode(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div className="text-red-500 text-sm text-center bg-red-500/10 py-2 px-3 rounded-lg">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className={`
          w-full flex justify-center py-3 px-4 rounded-lg text-sm font-semibold text-white
          transition-all duration-200 transform
          ${
            loading
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-red-600 hover:bg-red-500 active:scale-[0.98] hover:shadow-lg"
          }
        `}
      >
        {loading ? (
          <div className="flex items-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Se verifică...
          </div>
        ) : (
          "Verifică"
        )}
      </button>
    </form>
  );

  const loginContent = (
    <form className="space-y-6" onSubmit={handleSubmit}>
      {/* Email Input */}
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-foreground dark:text-foreground mb-1"
        >
          Adresă de email
        </label>
        <div className="relative mt-3">
          <InputGroup>
            <InputGroupInput
              id="email"
              name="email"
              type="email"
              required
              placeholder="nume@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <InputGroupAddon>
              <MailIcon />
            </InputGroupAddon>
          </InputGroup>
        </div>
      </div>

      {/* Password Input */}
      <div>
        <label
          htmlFor="password"
          className="block text-sm font-medium text-muted-foreground dark:text-gray-300 mb-1"
        >
          Parolă
        </label>
        <div className="relative mt-3">
          <InputGroup>
            <InputGroupInput
              id="password"
              name="password"
              type="password"
              required
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <InputGroupAddon>
              <Lock className="h-5 w-5 text-muted-foreground dark:text-gray-400" />
            </InputGroupAddon>
          </InputGroup>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="text-red-500 text-sm text-center bg-red-500/10 py-2 px-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        className={`
          w-full flex justify-center py-3 px-4 rounded-lg text-sm font-semibold text-white
          transition-all duration-200 transform
          ${
            loading
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-black hover:bg-black/80 active:scale-[0.98] hover:shadow-lg"
          }
        `}
      >
        {loading ? (
          <div className="flex items-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Se conectează...
          </div>
        ) : (
          "Conectare"
        )}
      </button>
    </form>
  );

  return (
    <div className="min-h-screen grid grid-cols-1  lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex items-center  justify-center py-12 px-4 sm:px-6 lg:px-8 border-r border-border dark:border-border bg-background dark:bg-background">
        <div className="w-full max-w-md">
          <div className="text-center mb-10 flex flex-col items-center gap-5">
            {/* <InlineLogo className="w-22 h-10" /> */}
            <h2 className="text-5xl font-bold text-foreground dark:text-gray-100">
              BFE
            </h2>
          </div>

          <div className="rounded-2xl p-8 shadow-2xl bg-white/90 dark:bg-white/5 backdrop-blur">
            {requiresTwoFactor ? twoFactorContent : loginContent}
          </div>
        </div>
      </div>

      {/* Right: image placeholder */}
      <div className="hidden lg:block relative grid-cols-2  bg-background">
        <img
          src="/login-background.avif"
          alt="Background"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
    </div>
  );
}
